import { useState } from "react";
import { Helmet } from "react-helmet-async";
import useAuthentication from "../../hooks/useAuthentication";
import useGoToLogin from "../../hooks/useGoToLogin";
import { useFollowUser } from "../../hooks/useFollowActions";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Rating,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import NgwaahiaIcon from "../../common-components/NgwaahiaIcon";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import ReportDialog from "../../common-components/ReportDialog";
import UnfollowConfirmDialog from "../../common-components/UnfollowConfirmDialog";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import EditProfileDialog from "./EditProfileDialog";
import ExperienceDialog from "./ExperienceDialog";
import EducationDialog, { LEVEL_LABELS } from "./EducationDialog";
import { useQuery } from "@tanstack/react-query";
import { generatePath, Link, useParams } from "react-router-dom";
import {
  getPublicUser,
  getUserShops,
} from "../../utility/httpActions/userActions";
import { resolveImageUri } from "../../utility/constants";
import { frontendRoutes } from "../../utility/frontendRoutes";
import { buildShopPath } from "../../utility/utilityFunctions";
import EntityErrorPage from "../../common-components/NotFoundPage";

const formatYearRange = (exp) => {
  const start = exp.startYear || "";
  if (exp.isCurrent) return `${start} – Present`;
  return exp.endYear ? `${start} – ${exp.endYear}` : String(start);
};

const formatJoinedDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const InfoRow = ({ icon: Icon, text, muted }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
    <Icon sx={{ fontSize: 15, color: "#9ca3af", flexShrink: 0 }} />
    <Typography
      variant="body2"
      color={muted ? "text.disabled" : "text.secondary"}
      sx={{ fontSize: 13 }}
    >
      {text}
    </Typography>
  </Box>
);

const ShopCard = ({ shop }) => {
  const avatarUri = shop.profilePhoto
    ? resolveImageUri(shop.profilePhoto)
    : null;
  return (
    <Box
      component={Link}
      to={buildShopPath(shop)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 2,
        borderBottom: "1px solid #f3f4f6",
        textDecoration: "none",
        color: "inherit",
        "&:last-child": { borderBottom: "none" },
        "&:hover .shop-name": { color: "#046287" },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "#dbeafe",
          flexShrink: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {avatarUri ? (
          <ImageWithLoader
            src={avatarUri}
            alt={shop.shopName}
            sx={{ borderRadius: "50%" }}
          />
        ) : (
          <NgwaahiaIcon size={18} color="#046287" />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          className="shop-name"
          variant="body2"
          fontWeight={600}
          sx={{
            transition: "color 0.15s",
            mb: 0.25,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {shop.shopName}
          {shop.isIdentityVerified && <VerifiedBadge size={13} />}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Shop owner
        </Typography>
        {shop.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: 12,
              mt: 0.5,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {shop.description}
          </Typography>
        )}
        {shop.rating > 0 && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
          >
            <Rating
              value={shop.rating}
              precision={0.1}
              readOnly
              size="small"
              sx={{ fontSize: 12 }}
            />
            {shop.numReviews > 0 && (
              <Typography variant="caption" color="text.secondary">
                ({shop.numReviews})
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ExperienceCard = ({ exp, onEdit }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      py: 2,
      borderBottom: "1px solid #f3f4f6",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: exp.shopId ? "50%" : 1,
        bgcolor: exp.shopId ? "#dbeafe" : "#f5f5f5",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {exp.shopId ? (
        <NgwaahiaIcon size={18} color="#046287" />
      ) : (
        <WorkOutlineIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>
        {exp.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontSize={13}>
        {exp.organisation || exp.org}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {formatYearRange(exp)}
      </Typography>
      {exp.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: 12, mt: 0.5 }}
        >
          {exp.description}
        </Typography>
      )}
    </Box>
    {onEdit && (
      <IconButton
        size="small"
        onClick={() => onEdit(exp)}
        sx={{ alignSelf: "flex-start", color: "#9ca3af" }}
      >
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
);

const EducationCard = ({ edu, onEdit }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      py: 2,
      borderBottom: "1px solid #f3f4f6",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1,
        bgcolor: "#f5f5f5",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SchoolOutlinedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>
        {edu.institution}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontSize={13}>
        {LEVEL_LABELS.get(edu.level) || edu.level}
        {edu.fieldOfStudy ? ` · ${edu.fieldOfStudy}` : ""}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {formatYearRange(edu)}
      </Typography>
      {edu.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: 12, mt: 0.5 }}
        >
          {edu.description}
        </Typography>
      )}
    </Box>
    {onEdit && (
      <IconButton
        size="small"
        onClick={() => onEdit(edu)}
        sx={{ alignSelf: "flex-start", color: "#9ca3af" }}
      >
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
);

// Shared layout used by both own-profile and public-profile pages.
export const ProfileLayout = ({ user, shops = [], isOwnProfile = false }) => {
  const [bioExpanded, setBioExpanded] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [unfollowConfirmOpen, setUnfollowConfirmOpen] = useState(false);
  const [shareSnackbar, setShareSnackbar] = useState(null);
  const [experienceDialog, setExperienceDialog] = useState({
    open: false,
    data: null,
  });
  const [educationDialog, setEducationDialog] = useState({
    open: false,
    data: null,
  });

  const coverUri = user?.coverPhoto ? resolveImageUri(user.coverPhoto) : null;
  const avatarUri = user?.profilePhoto
    ? resolveImageUri(user.profilePhoto)
    : null;

  const userId = user?.id || user?._id;

  const { currentUser, isLoggedIn } = useAuthentication();
  const goToLogin = useGoToLogin();
  const { mutate: toggleFollow, isPending: followPending } =
    useFollowUser(userId);

  const handleShare = async () => {
    const shareLink = user?.slug
      ? `${window.location.origin}/user/${user.slug}`
      : `${window.location.origin}/user/${userId}`;
    const shareData = {
      title: `${user?.name}'s profile on Ngwaahia`,
      url: shareLink,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareLink);
      setShareSnackbar("Link copied to clipboard");
    }
  };

  const followerCount = user?.followersCount ?? user?.followers?.length ?? 0;
  const followingCount = user?.followingCount ?? user?.following?.length ?? 0;

  // Follow-list visibility — mirrors mobile logic
  const followListVisibility = user?.followListVisibility ?? "PRIVATE";
  const isFollowedByViewer = user?.isFollowedByViewer ?? false;
  const followListIsVisible =
    isOwnProfile ||
    followListVisibility === "PUBLIC" ||
    (followListVisibility === "FOLLOWERS_ONLY" && isFollowedByViewer);

  const location = user?.location ?? user?.addresses?.[0] ?? null;
  const locationStr = location
    ? [location.city, location.state, location.country]
        .filter(Boolean)
        .join(", ")
    : null;

  const joinedDate = formatJoinedDate(user?.createdAt);

  const privacy = user?.privacySettings;
  const showEmail =
    !!user?.email && (privacy ? privacy.emailVisibility !== "PRIVATE" : true);
  const showPhone =
    !!user?.phoneNumber &&
    (privacy ? privacy.phoneVisibility !== "PRIVATE" : true);
  const showLocation =
    !!locationStr &&
    (privacy ? privacy.locationVisibility !== "PRIVATE" : true);

  const experiences = [...(user?.experiences || [])].sort((a, b) => {
    const endA = a.isCurrent ? Infinity : (a.endYear ?? a.startYear ?? 0);
    const endB = b.isCurrent ? Infinity : (b.endYear ?? b.startYear ?? 0);
    if (endA !== endB) return endB - endA;
    return (b.startYear ?? 0) - (a.startYear ?? 0);
  });

  const educations = [...(user?.education || [])].sort((a, b) => {
    const endA = a.isCurrent ? Infinity : (a.endYear ?? a.startYear ?? 0);
    const endB = b.isCurrent ? Infinity : (b.endYear ?? b.startYear ?? 0);
    if (endA !== endB) return endB - endA;
    return (b.startYear ?? 0) - (a.startYear ?? 0);
  });

  const hasExperience =
    shops.length > 0 || experiences.length > 0 || educations.length > 0;
  const BIO_CHAR_THRESHOLD = 160;

  // Note: this only updates the DOM after JS runs, so it fixes the browser
  // tab title and helps a JS-executing crawler (modern Googlebot) — it does
  // NOT produce link-preview cards on WhatsApp/Twitter/Facebook when a
  // profile URL is shared, since those scrapers don't execute JS. That needs
  // server-side meta injection in server.js.
  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      {!isOwnProfile && (
        <Helmet>
          <title>{user?.name} | Ngwaahia</title>
          <meta
            name="description"
            content={
              user?.bio
                ? user.bio.slice(0, 160)
                : `${user?.name} on Ngwaahia — view their profile and shops.`
            }
          />
        </Helmet>
      )}
      {/* Cover photo */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 160, md: 220 },
          bgcolor: "#046287",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {coverUri ? (
          <ImageWithLoader src={coverUri} alt="" />
        ) : (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.25)",
              fontWeight: 700,
              fontSize: { xs: 64, md: 80 },
            }}
          >
            {(user?.name || "U")[0].toUpperCase()}
          </Typography>
        )}
      </Box>

      {/* Avatar + actions row */}
      <Box sx={{ px: { xs: 2, md: 4 }, position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mt: "-40px",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: "3px solid white",
              overflow: "hidden",
              flexShrink: 0,
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              bgcolor: "#046287",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {avatarUri ? (
              <ImageWithLoader
                src={avatarUri}
                alt={user?.name}
                sx={{ borderRadius: "50%" }}
              />
            ) : (
              <Typography
                sx={{ color: "white", fontWeight: 700, fontSize: 28 }}
              >
                {(user?.name || "U")[0].toUpperCase()}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            {isOwnProfile ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setEditProfileOpen(true)}
                  sx={{
                    borderColor: "#046287",
                    color: "#046287",
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  Edit profile
                </Button>
                <Tooltip title="Account settings">
                  <IconButton
                    component={Link}
                    to={frontendRoutes.ACCOUNT_SETTINGS}
                    size="small"
                    sx={{ color: "#046287" }}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Button
                variant={isFollowedByViewer ? "outlined" : "contained"}
                size="small"
                disabled={followPending}
                onClick={() => {
                  if (!isLoggedIn) {
                    goToLogin();
                    return;
                  }
                  if (isFollowedByViewer) {
                    setUnfollowConfirmOpen(true);
                  } else {
                    toggleFollow(false);
                  }
                }}
                sx={{
                  borderRadius: 3,
                  fontWeight: 600,
                  minWidth: 96,
                  ...(isFollowedByViewer
                    ? { borderColor: "#046287", color: "#046287" }
                    : {
                        bgcolor: "#046287",
                        "&:hover": { bgcolor: "#035070" },
                      }),
                }}
              >
                {followPending
                  ? "…"
                  : isFollowedByViewer
                    ? "Following"
                    : "Follow"}
              </Button>
            )}
            <Tooltip title="Share profile">
              <IconButton
                size="small"
                onClick={handleShare}
                sx={{ color: "#046287" }}
              >
                <IosShareOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isOwnProfile && (
              <Tooltip title="Report">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!isLoggedIn) {
                      goToLogin();
                      return;
                    }
                    setReportOpen(true);
                  }}
                  sx={{ color: "#9ca3af" }}
                >
                  <FlagOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Name */}
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {user?.name}
          {user?.isIdentityVerified && <VerifiedBadge size={20} />}
        </Typography>

        {/* Info rows */}
        {showLocation && (
          <InfoRow icon={LocationOnOutlinedIcon} text={locationStr} />
        )}
        {showEmail && <InfoRow icon={MailOutlineIcon} text={user.email} />}
        {showPhone && (
          <InfoRow icon={PhoneOutlinedIcon} text={user.phoneNumber} />
        )}
        {user?.contactInfoRedacted && (
          <InfoRow
            icon={LockOutlinedIcon}
            text="Log in to view contact info"
            muted
          />
        )}
        {joinedDate && (
          <InfoRow
            icon={CalendarTodayOutlinedIcon}
            text={`Joined ${joinedDate}`}
          />
        )}

        {/* Follower / Following stats */}
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            mt: 1.5,
            mb: 0.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {followListIsVisible ? (
            <Box
              component={Link}
              to={generatePath(frontendRoutes.USER_FOLLOWERS, { userId })}
              sx={{
                textDecoration: "none",
                color: "inherit",
                "&:hover span": { color: "#046287" },
              }}
            >
              <Typography variant="body2" fontWeight={700} component="span">
                {followerCount}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {followerCount === 1 ? "Follower" : "Followers"}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight={700} component="span">
                {followerCount}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {followerCount === 1 ? "Follower" : "Followers"}
              </Typography>
            </Box>
          )}

          {followListIsVisible ? (
            <Box
              component={Link}
              to={generatePath(frontendRoutes.USER_FOLLOWING, { userId })}
              sx={{
                textDecoration: "none",
                color: "inherit",
                "&:hover span": { color: "#046287" },
              }}
            >
              <Typography variant="body2" fontWeight={700} component="span">
                {followingCount}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                Following
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight={700} component="span">
                {followingCount}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                Following
              </Typography>
            </Box>
          )}

          {/* "Follows you" chip — shown on other people's profiles when they follow the viewer */}
          {!isOwnProfile && user?.followsViewer && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                bgcolor: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                px: 1,
                py: 0.25,
              }}
            >
              <Typography
                sx={{ fontSize: 12, color: "#1d4ed8", fontWeight: 600 }}
              >
                Follows you
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Bio */}
      {(user?.bio || isOwnProfile) && (
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            mt: 2,
            pt: 2,
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ mb: 1, color: "#111827" }}
          >
            About
          </Typography>
          {user?.bio ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.7,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: bioExpanded ? "unset" : 4,
                }}
              >
                {user.bio}
              </Typography>
              {user.bio.length > BIO_CHAR_THRESHOLD && (
                <Typography
                  variant="body2"
                  color="#046287"
                  fontWeight={600}
                  sx={{ mt: 0.5, cursor: "pointer", fontSize: 12 }}
                  onClick={() => setBioExpanded((v) => !v)}
                >
                  {bioExpanded ? "Show less" : "See more"}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              variant="body2"
              color="#046287"
              fontWeight={600}
              sx={{ cursor: "pointer", fontSize: 13 }}
              onClick={() => setEditProfileOpen(true)}
            >
              + Add a bio
            </Typography>
          )}
        </Box>
      )}

      {/* Experience section */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 3 }}>
        <Divider sx={{ mb: 2.5 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ color: "#111827" }}
          >
            Experience
          </Typography>
          {isOwnProfile && (
            <IconButton
              size="small"
              onClick={() => setExperienceDialog({ open: true, data: null })}
              sx={{ color: "#046287" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {!isOwnProfile && user?.experienceHidden ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
            <Typography variant="body2" color="text.disabled">
              {user.experienceHidden === "LOGIN_REQUIRED"
                ? "Log in to view this section"
                : `Follow ${user?.firstName || user?.name?.split(" ")[0] || "this user"} to see their experience`}
            </Typography>
          </Box>
        ) : (
          <Box>
            {shops.map((shop) => (
              <ShopCard key={shop.id || shop._id} shop={shop} />
            ))}
            {experiences.map((exp, i) => (
              <ExperienceCard
                key={exp._id || exp.id || i}
                exp={exp}
                onEdit={
                  isOwnProfile
                    ? (e) => setExperienceDialog({ open: true, data: e })
                    : undefined
                }
              />
            ))}
            {!hasExperience && !isOwnProfile && (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
                sx={{ py: 2 }}
              >
                Nothing here yet.
              </Typography>
            )}
            {experiences.length === 0 && isOwnProfile && shops.length === 0 && (
              <Typography
                variant="body2"
                color="#046287"
                fontWeight={600}
                sx={{ cursor: "pointer", fontSize: 13, py: 1 }}
                onClick={() => setExperienceDialog({ open: true, data: null })}
              >
                + Add your first experience
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Education section */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 3 }}>
        <Divider sx={{ mb: 2.5 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ color: "#111827" }}
          >
            Education
          </Typography>
          {isOwnProfile && (
            <IconButton
              size="small"
              onClick={() => setEducationDialog({ open: true, data: null })}
              sx={{ color: "#046287" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {!isOwnProfile && user?.educationHidden ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
            <Typography variant="body2" color="text.disabled">
              {user.educationHidden === "LOGIN_REQUIRED"
                ? "Log in to view this section"
                : `Follow ${user?.firstName || user?.name?.split(" ")[0] || "this user"} to see their education`}
            </Typography>
          </Box>
        ) : (
          <Box>
            {educations.map((edu, i) => (
              <EducationCard
                key={edu._id || edu.id || i}
                edu={edu}
                onEdit={
                  isOwnProfile
                    ? (e) => setEducationDialog({ open: true, data: e })
                    : undefined
                }
              />
            ))}
            {educations.length === 0 && !isOwnProfile && (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
                sx={{ py: 2 }}
              >
                Nothing here yet.
              </Typography>
            )}
            {educations.length === 0 && isOwnProfile && (
              <Typography
                variant="body2"
                color="#046287"
                fontWeight={600}
                sx={{ cursor: "pointer", fontSize: 13, py: 1 }}
                onClick={() => setEducationDialog({ open: true, data: null })}
              >
                + Add your education
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ pb: 8 }} />

      {/* Edit profile dialog */}
      {isOwnProfile && (
        <EditProfileDialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
        />
      )}

      {/* Experience dialog */}
      {experienceDialog.open && (
        <ExperienceDialog
          open
          onClose={() => setExperienceDialog({ open: false, data: null })}
          experience={experienceDialog.data}
        />
      )}

      {/* Education dialog */}
      {educationDialog.open && (
        <EducationDialog
          open
          onClose={() => setEducationDialog({ open: false, data: null })}
          education={educationDialog.data}
        />
      )}

      {!isOwnProfile && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          entityType="USER"
          entityId={userId}
          entityName={user?.name || "this user"}
        />
      )}

      {!isOwnProfile && (
        <UnfollowConfirmDialog
          open={unfollowConfirmOpen}
          onClose={() => setUnfollowConfirmOpen(false)}
          onConfirm={() => {
            toggleFollow(true);
            setUnfollowConfirmOpen(false);
          }}
          entityName={user?.name}
          isPending={followPending}
        />
      )}

      <Snackbar
        open={!!shareSnackbar}
        autoHideDuration={3000}
        onClose={() => setShareSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setShareSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {shareSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ── Public profile page ── */

const UserPublicProfile = () => {
  const { userId } = useParams();
  const { currentUser } = useAuthentication();

  const isOwnProfile =
    !!currentUser &&
    (userId === currentUser.id ||
      userId === currentUser._id ||
      userId === currentUser.slug);

  const {
    data: user,
    isLoading: userLoading,
    error: userErrorObj,
  } = useQuery({
    queryKey: ["publicUser", userId],
    queryFn: () => getPublicUser(userId),
    enabled: !!userId && !isOwnProfile,
  });

  const { data: shopsData } = useQuery({
    queryKey: ["userShops", userId],
    queryFn: () =>
      getUserShops(isOwnProfile ? currentUser.id || currentUser._id : userId),
    enabled: !!userId,
  });

  if (!isOwnProfile && userLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const resolvedUser = isOwnProfile ? currentUser : user;

  if (!resolvedUser) {
    return (
      <EntityErrorPage
        errorStatus={userErrorObj?.status ?? 404}
        variant="user"
      />
    );
  }

  const shops = shopsData?.shops || [];

  return (
    <ProfileLayout
      user={resolvedUser}
      shops={shops}
      isOwnProfile={isOwnProfile}
    />
  );
};

export default UserPublicProfile;
